import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CalendarClock, CheckCircle2, Layers, Loader2, MapPin, Power, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { when } from "@/lib/rides";
import { Avatar } from "@/components/Profiles";
import { useAuth } from "@/context/AuthContext";
import { useMyProfile, dayTime } from "@/lib/rider";
import { GPS_LABEL, useDriverGps, useDriverState } from "@/lib/driverGps";
import { StatusBadge } from "@/components/RideStatus";
import { SectionTitle } from "@/components/rider/Nav";

export function DriveTime({ drive, online }) {
  if (!drive) return null;
  const hrs = (s) => `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}m`;
  if (drive.locked_until) {
    return <p className="mt-3 rounded-xl bg-amber-400/10 px-3 py-2 text-xs text-amber-200" data-testid="drive-rest-notice">4-hour rest after 8 hours Online. You can go back Online at {when(drive.locked_until)}.</p>;
  }
  const pct = Math.min(100, (drive.used_seconds / (drive.limit_hours * 3600)) * 100);
  return (
    <div className="mt-3" data-testid="drive-time">
      <div className="flex justify-between text-[11px] text-muted-foreground">
        <span>Drive time {hrs(drive.used_seconds)} of {drive.limit_hours}h</span>
        <span data-testid="drive-time-remaining">{online ? `${hrs(drive.remaining_seconds)} left` : "Paused"}</span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted"><div className={`h-full ${pct >= 90 ? "bg-amber-400" : "bg-primary"}`} style={{ width: `${pct}%` }} /></div>
      {drive.limit_reached && online && <p className="mt-2 text-xs text-amber-200" data-testid="drive-limit-reached">8 hours reached. Finish your current ride, then you'll go Offline for a 4-hour rest.</p>}
    </div>
  );
}

export function OnlineToggle() {
  const qc = useQueryClient();
  const { data: s } = useDriverState();
  const gps = useDriverGps();
  const [busy, setBusy] = useState(false);
  const online = !!s?.online;
  const resting = !online && !!s?.drive?.locked_until;
  const flip = async () => {
    setBusy(true);
    try {
      await api.post("/driver/online", { online: !online });
      toast.success(online ? "You're Offline. Scheduled rides stay assigned." : "You're Online and sharing your location");
      ["driverState", "nowRides"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`rounded-2xl border p-4 ${online ? "border-primary/60 bg-primary/10" : "border-white/10 bg-card"}`} data-testid="online-toggle-card">
      <button type="button" onClick={flip} disabled={busy || !s || resting} data-testid="online-toggle-button" className="flex w-full items-center gap-4 text-left">
        <span className={`grid h-14 w-14 shrink-0 place-items-center rounded-full transition-colors ${online ? "bg-primary text-primary-foreground shadow-[0_0_24px_hsl(var(--primary)/0.6)]" : "bg-muted text-muted-foreground"}`}>
          {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <Power className="h-6 w-6" />}
        </span>
        <span>
          <span className="block font-display text-2xl font-bold" data-testid="online-status">{online ? "ONLINE" : "OFFLINE"}</span>
          <span className="block text-xs text-muted-foreground">{online ? (s?.drive?.limit_reached ? "8 hours reached. No new Now Rides." : "Eligible for Now Rides. Tap to go Offline.") : "Not receiving Now Rides. Tap to go Online."}</span>
        </span>
      </button>
      <DriveTime drive={s?.drive} online={online} />
      <p className={`mt-3 flex items-center gap-1.5 text-xs ${gps.status === "live" ? "text-primary" : gps.status === "off" ? "text-muted-foreground" : "text-amber-300"}`} data-testid="gps-status">
        <MapPin className="h-3.5 w-3.5" /> {GPS_LABEL[gps.status]}{gps.error ? `: ${gps.error}` : ""}
      </p>
      {online && s?.next_ride && new Date(s.next_ride.scheduled_at) - Date.now() > 15 * 60000 && <p className="mt-1 text-[11px] text-muted-foreground" data-testid="rider-sees-from">Your next rider sees your location from {when(new Date(new Date(s.next_ride.scheduled_at) - 15 * 60000).toISOString())}.</p>}
      {s?.lock && <p className="mt-2 flex gap-1.5 rounded-xl bg-amber-400/10 px-3 py-2 text-xs text-amber-200" data-testid="now-lock-notice"><AlertTriangle className="h-3.5 w-3.5 shrink-0" />{s.lock.message}{s.lock.reason === "scheduled_soon" ? ` (pickup ${when(s.lock.scheduled_at)})` : ""}</p>}
      <p className="mt-2 text-[11px] text-muted-foreground">Going Offline never cancels or changes your accepted scheduled rides.</p>
    </div>
  );
}

export function DriverGreeting({ onProfile }) {
  const { user } = useAuth();
  const { data: p } = useMyProfile();
  return (
    <button type="button" onClick={onProfile} className="flex w-full items-center gap-3 text-left" data-testid="driver-greeting">
      <Avatar photo={p?.photo} name={user?.name} size="h-12 w-12" testid="driver-avatar" />
      <span className="min-w-0"><span className="block text-xs text-muted-foreground">Driver</span>
        <span className="block truncate font-display text-xl font-bold" data-testid="driver-name">{user?.name}</span></span>
      <span className="ml-auto rounded-full border border-white/15 px-3 py-1 text-xs font-semibold text-muted-foreground">Profile</span>
    </button>
  );
}

function Stat({ icon: Icon, label, value, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-card p-3 text-left transition-colors hover:border-primary/50">
      <Icon className="h-4 w-4 shrink-0 text-primary" />
      <span className="min-w-0"><span className="block truncate text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">{label}</span>
        <span className="block font-display text-lg font-bold" data-testid={`${testid}-value`}>{value}</span></span>
    </button>
  );
}

export function DriverStats({ go, counts }) {
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="driver-stats">
      <Stat icon={Zap} label="Today's rides" value={counts.today} onClick={() => go("calendar")} testid="stat-today" />
      <Stat icon={Layers} label="Available sets" value={counts.available} onClick={() => go("scheduled", "available")} testid="stat-available-sets" />
      <Stat icon={CalendarClock} label="Accepted sets" value={counts.accepted} onClick={() => go("scheduled", "mine")} testid="stat-accepted-sets" />
      <Stat icon={CheckCircle2} label="Completed" value={counts.completed} onClick={() => go("history")} testid="stat-completed" />
    </div>
  );
}

export function RideRow({ ride, onOpen }) {
  const { day, time } = dayTime(ride.scheduled_at);
  return (
    <button type="button" onClick={() => onOpen(ride)} data-testid={`driver-ride-row-${ride.id}`}
      className="flex w-full items-center gap-3 rounded-2xl border border-white/10 bg-card p-3 text-left transition-colors hover:border-primary/50">
      <span className="w-16 shrink-0"><span className="block font-display text-base font-bold">{time}</span><span className="block text-[10px] text-muted-foreground">{day}</span></span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold">{ride.pickup.name} → {ride.dropoff.name}</span>
        <span className="block truncate text-xs text-muted-foreground">{ride.rider_name}{ride.split_id ? ` · Leg ${ride.split_leg}/2` : ""}{ride.stops?.length ? ` · ${ride.stops.length} stops` : ""}</span>
      </span>
      <StatusBadge status={ride.status} />
    </button>
  );
}

export function NextRide({ onOpen }) {
  const { data: s } = useDriverState();
  const r = s?.next_ride;
  return (
    <section className="space-y-2" data-testid="driver-next-ride">
      <SectionTitle>Next scheduled ride</SectionTitle>
      {r ? <RideRow ride={r} onOpen={onOpen} /> : <p className="text-sm text-muted-foreground">Nothing scheduled yet.</p>}
      {r && <p className="text-[11px] text-muted-foreground">Pickup {when(r.scheduled_at)}</p>}
    </section>
  );
}
